import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Input,
    Select,
    Button,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Statistic,
    message,
    Tooltip,
    Modal,
    Form
} from 'antd';
import {
    ReloadOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    StopOutlined,
    EyeOutlined,
    LockOutlined,
    UnlockOutlined,
    DeleteOutlined,
    CloudSyncOutlined
} from '@ant-design/icons';
import { Client, ClientFilters, UserStatus } from '../types/admin.types';
import adminApi from '../services/adminApi';
import { StatusBadge } from '../components/common/StatusBadge';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Интерфейс для статистики с бэкенда
interface ClientsStats {
    total: number;
    active: number;
    pending: number;
    rejected: number;
    verified: number;
    blocked: number;
    approved: number;
    needsRevision: number;
    from1C: number;
}

export const ClientsPage: React.FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<ClientsStats | null>(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });
    const [filters, setFilters] = useState<ClientFilters & { from1CFilter?: string }>({
        status: 'ALL',
        searchTerm: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        from1CFilter: 'ALL'
    });
    const [blockModalVisible, setBlockModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [blockForm] = Form.useForm();

    // Загрузка статистики при монтировании
    useEffect(() => {
        loadStats();
    }, []);

    useEffect(() => {
        loadClients();
    }, [pagination.current, pagination.pageSize, filters]);

    const loadStats = async () => {
        try {
            const response = await adminApi.getClientsStats();
            if (response.data.success && response.data.data) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Ошибка загрузки статистики:', error);
        }
    };

    const loadClients = async () => {
        setLoading(true);
        try {
            const response = await adminApi.getClients(
                filters,
                pagination.current - 1,
                pagination.pageSize
            );

            if (response.data.success && response.data.data) {
                setClients(response.data.data.content);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.data!.totalElements
                }));
            } else {
                message.error('Ошибка загрузки клиентов');
            }
        } catch (error) {
            console.error('Ошибка загрузки клиентов:', error);
            message.error('Ошибка загрузки клиентов');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setFilters(prev => ({ ...prev, searchTerm: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleStatusFilter = (status: string) => {
        setFilters(prev => ({ ...prev, status }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
        setFilters(prev => ({ ...prev, sortBy, sortOrder }));
    };

    const handleBlockClient = (client: Client) => {
        setSelectedClient(client);
        setBlockModalVisible(true);
    };

    const handleDeleteClient = (client: Client) => {
        setSelectedClient(client);
        setDeleteModalVisible(true);
    };

    const handleUnblockClient = async (client: Client) => {
        try {
            await adminApi.unblockClient(client.id);
            message.success('Клиент успешно разблокирован');
            loadClients();
        } catch (error) {
            console.error('Ошибка разблокировки клиента:', error);
            message.error('Ошибка при разблокировке клиента');
        }
    };

    const handleBlockSubmit = async (values: { reason: string }) => {
        if (!selectedClient) return;

        try {
            await adminApi.blockClient(selectedClient.id, values.reason);
            message.success('Клиент успешно заблокирован');
            setBlockModalVisible(false);
            setSelectedClient(null);
            blockForm.resetFields();
            loadClients();
        } catch (error) {
            console.error('Ошибка блокировки клиента:', error);
            message.error('Ошибка при блокировке клиента');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedClient) return;

        try {
            await adminApi.deleteClient(selectedClient.id);
            message.success('Клиент успешно удален');
            setDeleteModalVisible(false);
            setSelectedClient(null);
            loadClients();
        } catch (error) {
            console.error('Ошибка удаления клиента:', error);
            message.error('Ошибка при удалении клиента');
        }
    };

    // Фильтрация по флагу 1С (клиентская фильтрация на загруженных данных)
    const filteredClients = filters.from1CFilter === 'ALL' 
        ? clients 
        : filters.from1CFilter === 'FROM_1C'
            ? clients.filter(c => c.company?.from1C)
            : clients.filter(c => !c.company?.from1C);


    const getVerificationStatus = (client: Client) => {
        const verifications = [];
        if (client.isEmailVerified) {
            verifications.push(<Tag key="email" color="green" icon={<MailOutlined />}>Email</Tag>);
        }
        if (client.isPhoneVerified) {
            verifications.push(<Tag key="phone" color="green" icon={<PhoneOutlined />}>Телефон</Tag>);
        }
        return verifications.length > 0 ? verifications : <Tag color="default">Не верифицирован</Tag>;
    };

    const columns = [
        {
            title: 'Клиент',
            key: 'client',
            render: (_: any, record: Client) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>
                        {record.firstName} {record.lastName}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.login}
                    </Text>
                </div>
            ),
            width: 200
        },
        {
            title: 'Контакты',
            key: 'contacts',
            render: (_: any, record: Client) => (
                <div>
                    <div style={{ marginBottom: '4px' }}>
                        <PhoneOutlined style={{ marginRight: '4px' }} />
                        {record.phoneNumber}
                    </div>
                    {record.email && (
                        <div>
                            <MailOutlined style={{ marginRight: '4px' }} />
                            {record.email}
                        </div>
                    )}
                </div>
            ),
            width: 200
        },
        {
            title: 'Компания',
            key: 'company',
            render: (_: any, record: Client) => (
                record.company ? (
                    <div>
                        <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {record.company.companyName}
                            {record.company.from1C && (
                                <Tag color="blue" icon={<CloudSyncOutlined />} style={{ marginLeft: '4px' }}>
                                    1С
                                </Tag>
                            )}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            БИН: {record.company.bin}
                        </Text>
                    </div>
                ) : (
                    <Text type="secondary">Не указана</Text>
                )
            ),
            width: 280
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: UserStatus) => (
                <StatusBadge status={status} />
            ),
            width: 150
        },
        {
            title: 'Верификация',
            key: 'verification',
            render: (_: any, record: Client) => getVerificationStatus(record),
            width: 150
        },
        {
            title: 'Дата регистрации',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
            sorter: true,
            width: 150
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Client) => (
                <Space>
                    <Tooltip title="Просмотр деталей">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            size="small"
                        />
                    </Tooltip>
                    {record.status === UserStatus.BLOCKED ? (
                        <Tooltip title="Разблокировать">
                            <Button
                                type="text"
                                icon={<UnlockOutlined />}
                                size="small"
                                onClick={() => handleUnblockClient(record)}
                                style={{ color: '#52c41a' }}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Заблокировать">
                            <Button
                                type="text"
                                icon={<LockOutlined />}
                                size="small"
                                onClick={() => handleBlockClient(record)}
                                style={{ color: '#ff4d4f' }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Удалить">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={() => handleDeleteClient(record)}
                            style={{ color: '#ff4d4f' }}
                        />
                    </Tooltip>
                </Space>
            ),
            width: 120
        }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="mb-8">
                <Title level={2}>Управление клиентами</Title>
                <Text type="secondary">
                    Просмотр и управление всеми зарегистрированными клиентами
                </Text>
            </div>

            {/* Статистика (данные с бэкенда) */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Всего"
                            value={stats?.total ?? 0}
                            prefix={<UserOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Активные"
                            value={stats?.active ?? 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="На модерации"
                            value={stats?.pending ?? 0}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Отклоненные"
                            value={stats?.rejected ?? 0}
                            prefix={<StopOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Верифицированные"
                            value={stats?.verified ?? 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Заблокированные"
                            value={stats?.blocked ?? 0}
                            prefix={<LockOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4} lg={3}>
                    <Card size="small">
                        <Statistic
                            title="Из 1С"
                            value={stats?.from1C ?? 0}
                            prefix={<CloudSyncOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Фильтры */}
            <Card className="mb-6">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Search
                            placeholder="Поиск по имени, телефону, email или БИН..."
                            allowClear
                            onSearch={handleSearch}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                        <Select
                            value={filters.status}
                            onChange={handleStatusFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="ALL">Все статусы</Option>
                            <Option value="ACTIVE">Активные</Option>
                            <Option value="VERIFIED">Верифицированные</Option>
                            <Option value="PENDING_MODERATION">На модерации</Option>
                            <Option value="REJECTED">Отклоненные</Option>
                            <Option value="BLOCKED">Заблокированные</Option>
                            <Option value="NEEDS_REVISION">Требуют доработки</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                        <Select
                            value={filters.from1CFilter}
                            onChange={(value) => setFilters(prev => ({ ...prev, from1CFilter: value }))}
                            style={{ width: '100%' }}
                        >
                            <Option value="ALL">Все источники</Option>
                            <Option value="FROM_1C">
                                <CloudSyncOutlined style={{ marginRight: 4 }} />
                                Из 1С
                            </Option>
                            <Option value="NOT_FROM_1C">Не из 1С</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                        <Select
                            value={filters.sortBy}
                            onChange={(value) => handleSort(value, filters.sortOrder)}
                            style={{ width: '100%' }}
                        >
                            <Option value="createdAt">По дате</Option>
                            <Option value="firstName">По имени</Option>
                            <Option value="status">По статусу</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={6} md={4} lg={3}>
                        <Select
                            value={filters.sortOrder}
                            onChange={(value) => handleSort(filters.sortBy, value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="desc">По убыванию</Option>
                            <Option value="asc">По возрастанию</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={3}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadClients}
                            loading={loading}
                            style={{ width: '100%' }}
                        >
                            Обновить
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Таблица клиентов */}
            <Card title={
                <Space>
                    <span>Список клиентов</span>
                    {filters.from1CFilter === 'FROM_1C' && (
                        <Tag color="blue" icon={<CloudSyncOutlined />}>Показаны только из 1С</Tag>
                    )}
                </Space>
            }>
                <Table
                    columns={columns}
                    dataSource={filteredClients}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: filters.from1CFilter === 'ALL' ? pagination.total : filteredClients.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} из ${total} клиентов`,
                        onChange: (page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize || 20
                            }));
                        }
                    }}
                />
            </Card>

            {/* Модальное окно блокировки */}
            <Modal
                title="Заблокировать клиента"
                open={blockModalVisible}
                onCancel={() => {
                    setBlockModalVisible(false);
                    setSelectedClient(null);
                    blockForm.resetFields();
                }}
                footer={null}
            >
                {selectedClient && (
                    <div>
                        <p>Вы уверены, что хотите заблокировать клиента:</p>
                        <p><strong>{selectedClient.firstName} {selectedClient.lastName}</strong></p>
                        <p>Email: {selectedClient.email || 'Не указан'}</p>
                        <p>Телефон: {selectedClient.phoneNumber || 'Не указан'}</p>

                        <Form
                            form={blockForm}
                            onFinish={handleBlockSubmit}
                            layout="vertical"
                        >
                            <Form.Item
                                name="reason"
                                label="Причина блокировки"
                                rules={[{ required: true, message: 'Укажите причину блокировки' }]}
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Введите причину блокировки клиента..."
                                />
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" danger>
                                        Заблокировать
                                    </Button>
                                    <Button onClick={() => {
                                        setBlockModalVisible(false);
                                        setSelectedClient(null);
                                        blockForm.resetFields();
                                    }}>
                                        Отмена
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Modal>

            {/* Модальное окно удаления */}
            <Modal
                title="Удалить клиента"
                open={deleteModalVisible}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setSelectedClient(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setDeleteModalVisible(false);
                        setSelectedClient(null);
                    }}>
                        Отмена
                    </Button>,
                    <Button key="delete" type="primary" danger onClick={handleDeleteConfirm}>
                        Удалить
                    </Button>
                ]}
            >
                {selectedClient && (
                    <div>
                        <p><strong>Внимание!</strong> Это действие нельзя отменить.</p>
                        <p>Вы уверены, что хотите удалить клиента:</p>
                        <p><strong>{selectedClient.firstName} {selectedClient.lastName}</strong></p>
                        <p>Email: {selectedClient.email || 'Не указан'}</p>
                        <p>Телефон: {selectedClient.phoneNumber || 'Не указан'}</p>
                        <p>Все данные клиента будут удалены из системы.</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};
